/* eslint-disable @typescript-eslint/no-explicit-any */
import { RawEmailCard } from "@/components/popups/email-health-test-results";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { allEmailProvidersDetails } from "@/lib/utils";
import { EmailDetailResponse } from "@/types/inboxPlacements.types";
import {
  ArrowUpRight,
  Calendar,
  Hash,
  Mail,
  PanelRight,
  Usb,
  User,
  X
} from "lucide-react";
import EmailHealthBlacklist from "../email-health/email-health-report/email-health-blacklist";
import EmailHealthDeliveryChain from "../email-health/email-health-report/email-health-delivery-chain";
import EmailHealthOpenRelay from "../email-health/email-health-report/email-health-open-relay";

// Skeleton component for email details section
export function EmailDetailsSkeleton() {
  return (
    <Sheet>
      <SheetTrigger>
        <div className="flex items-center justify-between w-full px-3 py-2 rounded-md hover:bg-gray-50 transition-colors">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-8 w-24" />
        </div>
      </SheetTrigger>
    </Sheet>
  );
}

export function EmailDetails({
  data,
  email,
  isLoading,
  providerKey,
  showAction,
}: {
  data: EmailDetailResponse;
  email: string;
  isLoading?: boolean;
  providerKey: string;
  showAction?: boolean | false;
}) {
  const currentProvider =
    allEmailProvidersDetails[
      providerKey as keyof typeof allEmailProvidersDetails
    ];

  // If loading, render the skeleton sheet trigger only
  if (isLoading) {
    return (
      <Sheet>
        <SheetTrigger>
          {showAction ? (
            <div>
            <PanelRight className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
          ) : (
            <div className="flex text-primary items-center gap-1">
            <span className="text-sm  underline max-w-[250px] truncate">
              {email}
            </span>
            <PanelRight className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
          )}
        </SheetTrigger>
        <SheetContent className="max-w-full w-full sm:max-w-4xl p-0">
          <SheetClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary z-40">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </SheetClose>
          <ScrollArea className="h-[calc(100vh-32px)] p-4 md:p-6">
            <SheetHeader className="space-y-2 mb-3">
              <SheetTitle>Email Details</SheetTitle>
            </SheetHeader>
            <div className="space-y-4">
              {/* Email Details Card Skeleton */}
              <Card>
                <CardHeader className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1 w-full">
                      <Skeleton className="h-6 w-full max-w-32" />
                      <Skeleton className="h-4 w-full max-w-64" />
                    </div>
                    <Skeleton className="h-4 w-4 rounded-full" />
                  </div>
                </CardHeader>
                <CardContent className="p-4 space-y-4 text-sm border-t">
                  {[1, 2, 3, 4, 5, 6].map((index) => (
                    <div key={index} className="flex items-center">
                      <Skeleton className="w-5 h-5 mr-2 rounded-full shrink-0" />
                      <Skeleton className="w-full max-w-16 h-5" />
                      <Skeleton className="ml-2 w-full max-w-64 h-5" />
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Raw Email Skeleton */}
              <Card className="w-full">
                <CardHeader className="flex-row justify-between p-4 space-y-0">
                  <div className="flex items-center gap-2 w-full">
                    <Skeleton className="h-5 w-full max-w-24" />
                    <Skeleton className="h-3 w-3 rounded-full shrink-0" />
                  </div>
                  <div className="flex items-center gap-2 w-full justify-end">
                    <Skeleton className="h-8 w-full max-w-24" />
                    <Skeleton className="h-4 w-4 rounded-full shrink-0" />
                  </div>
                </CardHeader>
                <CardContent className="border-t p-4">
                  <Skeleton className="h-[400px] w-full" />
                </CardContent>
              </Card>

              {/* Blacklist Status Skeleton */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-7 w-full max-w-40" />
                  <Skeleton className="h-4 w-4 rounded-full shrink-0" />
                </div>

                <div className="grid gap-3">
                  {[1, 2, 3].map((index) => (
                    <Card key={index}>
                      <CardHeader className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 w-full">
                            <Skeleton className="h-4 w-4 rounded-full shrink-0" />
                            <Skeleton className="h-5 w-full max-w-48" />
                            <Skeleton className="h-3 w-3 rounded-full" />
                          </div>
                          <Skeleton className="h-4 w-4 rounded-full shrink-0" />
                        </div>
                        <Skeleton className="h-4 w-full max-w-64 mt-2" />
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Email Delivery Chain Skeleton */}
              <div>
                <Skeleton className="h-7 w-full max-w-48 mb-2" />
                <div className="grid gap-4">
                  <Card>
                    <CardHeader className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 w-full">
                          <Skeleton className="h-4 w-4 rounded-full shrink-0" />
                          <Skeleton className="h-5 w-full max-w-32" />
                          <Skeleton className="h-3 w-3 rounded-full shrink-0" />
                        </div>
                        <Skeleton className="h-4 w-4 rounded-full shrink-0" />
                      </div>
                      <Skeleton className="h-4 w-full max-w-64 mt-2" />
                    </CardHeader>
                  </Card>
                </div>
              </div>

              {/* Open Relay Check Skeleton */}
              <div>
                <Skeleton className="h-7 w-full max-w-40 mb-2" />
                <div className="grid gap-4">
                  <Card>
                    <CardHeader className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 w-full">
                          <Skeleton className="h-4 w-4 rounded-full shrink-0" />
                          <Skeleton className="h-5 w-full max-w-40" />
                          <Skeleton className="h-3 w-3 rounded-full shrink-0" />
                        </div>
                        <Skeleton className="h-4 w-4 rounded-full shrink-0" />
                      </div>
                      <Skeleton className="h-4 w-full max-w-48 mt-2" />
                    </CardHeader>
                  </Card>
                </div>
              </div>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet
      onOpenChange={(open) => {
        if (open) {
          window.cioanalytics?.track("inbox_placement_email_details_opened", {
            email: email,
          });
        }
      }}
    >
      <SheetTrigger>
        {showAction ? (
          <div className="inline-flex items-center justify-center rounded-md border bg-background p-1.5 text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <PanelRight className="h-4 w-4" />
            <span className="sr-only">View email details</span>
          </div>
        ) : (
          <div className="flex text-primary items-center gap-1">
            <span className="text-sm  underline max-w-[250px] truncate">
              {email}
            </span>
            <PanelRight className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
        )}
      </SheetTrigger>
      <SheetContent
        showCloseButton={false}
        className="max-w-full w-full sm:max-w-4xl p-0"
      >
        <SheetClose
          tabIndex={-1}
          className="absolute right-3 sm:right-4 top-3 sm:top-4 rounded-sm ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary z-40 p-2 bg-background border"
        >
          <X className="h-4 w-4 text-foreground" />
          <span className="sr-only">Close</span>
        </SheetClose>
        <ScrollArea className="h-[calc(100vh-32px)] p-3 sm:p-6">
          <SheetHeader className="space-y-1.5 mb-2 sm:mb-3">
            <SheetTitle>Email Details</SheetTitle>
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <span className="font-semibold">Provider:</span>{" "}
              {currentProvider?.icon && (
                <img
                  src={currentProvider?.icon}
                  alt={currentProvider?.name}
                  width={20}
                  height={20}
                />
              )}
              <span>{currentProvider?.name}</span>
            </div>
          </SheetHeader>
          <div className="space-y-4">
            {/* Email Details Card */}
            <Card>
              <CardHeader className="bg-muted/50 border-b p-3 sm:p-4">
                <CardTitle>Email Information</CardTitle>
                <CardDescription className="text-sm">
                  General information about the tested email
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4 sm:pt-6 p-3 sm:p-4 space-y-3 sm:space-y-4 text-sm">
                <div className="flex flex-col md:flex-row md:items-center gap-1 sm:gap-2">
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 text-blue-600" />
                    <span className="font-semibold">To:</span>
                  </div>
                  <span className="break-all md:ml-2">{data?.to}</span>
                </div>
                <div className="flex flex-col md:flex-row md:items-center gap-1 sm:gap-2">
                  <div className="flex items-center">
                    <User className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 text-blue-600" />
                    <span className="font-semibold">From:</span>
                  </div>
                  <span className="break-all md:ml-2">
                    {typeof data?.from === "object"
                      ? data?.from?.text
                      : data?.from}
                  </span>
                </div>
                <div className="flex flex-col md:flex-row md:items-center gap-1 sm:gap-2">
                  <div className="flex items-center">
                    <Usb className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 text-blue-600" />
                    <span className="font-semibold">IP:</span>
                  </div>
                  <span className="break-all md:ml-2">{data?.senderIP}</span>
                </div>
                <div className="flex flex-col md:flex-row md:items-center gap-1 sm:gap-2">
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 text-blue-600" />
                    <span className="font-semibold">Subject:</span>
                  </div>
                  <span className="break-all md:ml-2">{data?.subject}</span>
                </div>
                <div className="flex flex-col md:flex-row md:items-center gap-1 sm:gap-2">
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 text-blue-600" />
                    <span className="font-semibold">Date:</span>
                  </div>
                  <span className="md:ml-2">
                    {data?.date
                      ? new Date(data?.date).toLocaleDateString("en-GB", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })
                      : "N/A"}
                  </span>
                </div>
                <div className="flex flex-col md:flex-row md:items-center gap-1 sm:gap-2">
                  <div className="flex items-center">
                    <Hash className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 shrink-0 text-blue-600" />
                    <span className="font-semibold whitespace-nowrap">
                      Message ID:
                    </span>
                  </div>
                  <span className="break-all md:ml-2">
                    {data?.messageId || "N/A"}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* email content card
            <Card>
              <Collapsible className="w-full" defaultOpen>
                <CollapsibleTrigger asChild>
                  <CardHeader className="hover:bg-muted/50 transition-colors p-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <CardTitle>Email Content</CardTitle>
                        <CardDescription>Message body content</CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center md:mr-2 text-xs text-muted-foreground">
                          <StyledTooltip description="The total size of the email template">
                            <div className="flex text-sm items-center gap-1 bg-muted/40 px-2 py-1 rounded-md">
                              <Database className="w-3 h-3 text-foreground" />
                              <span>
                                {(
                                  (data?.htmlContent?.length || 0) / 1024
                                ).toFixed(1)}{" "}
                                KB
                              </span>
                            </div>
                          </StyledTooltip>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDownloadEmailContent}
                        >
                          <Download className="w-4 h-4" />
                          Download
                        </Button>
                        <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform duration-200 [&[data-state=open]>svg]:rotate-180" />
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent className="">
                  <CardContent className="p-4 text-sm border-t">
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-semibold mb-2">Plain Text</h3>
                        <div className="bg-gray-50 p-4 rounded-md whitespace-pre-wrap">
                          {data?.plainText}
                        </div>
                      </div>
                      <div>
                        <div className="rounded-md">
                          <SanitizedHtmlViewer
                            html={data?.htmlContent as string}
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card> */}

            {/* Raw Email */}
            <RawEmailCard raw={data?.rawContent || ""} />

            {/* Blacklist Status */}
            <EmailHealthBlacklist
              domainBlacklist={data?.domainBlacklist || []}
              mxServerBlacklist={data?.mxServerBlacklists || []}
              smtpBlacklist={data?.smtpBlacklist || []}
            />

            {/* Email Delivery Chain */}
            <EmailHealthDeliveryChain
              deliveryChain={data?.formattedDeliveryChain || []}
            />

            {/* open relay check card */}
            <EmailHealthOpenRelay relaycheck={data?.relaycheck} />
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
